// PHI: converts parsed census rows into the member records submitted with the
// RFP.

import camelCase from "lodash/camelCase";

import { GroupMember, ProcessedRow } from "./types";

function formatEcosystem(data: ProcessedRow[]): GroupMember[] {
  return data.map((row, i) => {
    const employmentStatus = toString(row["employment status"]?.processed);
    return {
      employeeCode: `${i + 1}`,
      dependentRelationship: camelCase(
        toCamelCaseStr(row.relationship?.processed),
      ),
      employmentType: toString(row["employment type"]?.processed),
      gender: toCamelCaseStr(row.gender?.processed),
      birthDate: toString(row["date of birth"]?.processed),
      postalCode: toString(row.zip?.processed),
      employmentStatus:
        employmentStatus === "Leave" ? "onLeave" : camelCase(employmentStatus),
      employeeJobTitle: toString(row.occupation?.processed),
      benefitClassName: toString(row["eligibility class"]?.processed),
      annualIncome: Number(row["annual salary"]?.processed ?? 0),
      coverage: getCoverage(row),
    };
  });
}

function getCoverage(row: ProcessedRow): GroupMember["coverage"] {
  const coverage: GroupMember["coverage"] = [];
  processDentalVisionCoverage("dental", row, coverage);
  processDentalVisionCoverage("vision", row, coverage);
  processBasicLifeCoverage("ee", row, coverage);
  processStdLtdCoverage("std", row, coverage);
  processStdLtdCoverage("ltd", row, coverage);
  processVoluntaryCoverage("life", "ee", "voluntaryLife", row, coverage);
  processVoluntaryCoverage("ad&d", "ee", "adnd", row, coverage);

  return coverage;
}

function processDentalVisionCoverage(
  coverageType: "vision" | "dental",
  row: ProcessedRow,
  coverage: GroupMember["coverage"],
): void {
  const planCoverage = row[`${coverageType} plan coverage`]?.processed;
  if (planCoverage) {
    const isWaived = planCoverage === "Waived";
    coverage.push({
      coverageType,
      isWaived,
      coverageTierCode: isWaived
        ? "waived"
        : TIERS_MAPPING[toString(planCoverage)],
      benefitPlanName: !isWaived
        ? toString(row[`${coverageType} plan name`]?.processed)
        : undefined,
    });
  }
}
function processBasicLifeCoverage(
  tier: "ee",
  row: ProcessedRow,
  coverage: GroupMember["coverage"],
): void {
  const basicLifeEE = row[`basic life/ad&d ${tier} coverage`]?.processed;
  if (basicLifeEE) {
    const isWaived = basicLifeEE === "Waived";
    coverage.push({
      coverageType: "basicLife",
      isWaived,
      coverageTierCode: isWaived
        ? "waived"
        : COVERAGE_ABBREVIATION_MAPPING[tier],
    });
  }
}
function processStdLtdCoverage(
  coverageType: "std" | "ltd",
  row: ProcessedRow,
  coverage: GroupMember["coverage"],
): void {
  const planCoverage = row[`${coverageType} election`]?.processed;
  if (planCoverage) {
    const isWaived = planCoverage === "Waived";
    coverage.push({
      coverageType,
      isWaived,
      coverageTierCode: isWaived ? "waived" : undefined,
    });
  }
}
function processVoluntaryCoverage(
  insurance: "life" | "ad&d",
  tier: "ee",
  coverageType: "voluntaryLife" | "adnd",
  row: ProcessedRow,
  coverage: GroupMember["coverage"],
): void {
  const voluntary =
    row[`vol ${insurance} ${tier} coverage`]?.processed ?? false;
  if (voluntary === false) {
    return;
  }

  const isWaived = voluntary === "Waived";
  const cov = {
    coverageType,
    isWaived,
    coverageTierCode: isWaived ? "waived" : COVERAGE_ABBREVIATION_MAPPING[tier],
  } as const;
  if (typeof voluntary === "number") {
    coverage.push({
      ...cov,
      coverageAmount: voluntary,
    });
  } else {
    coverage.push(cov);
  }
}

function toString(val: unknown): string {
  return `${val ?? ""}`;
}
function toCamelCaseStr(val: unknown): string {
  return camelCase(toString(val));
}

const TIERS_MAPPING: Record<string, string> = {
  "EE Only": "employee",
  "EE + SP": "employeeSpouse",
  "EE + DP": "employeeDependent",
  "EE + CH": "employeeChildren",
  "EE + Fam": "employeeFamily",
  "EE + 1": "employeeDependent",
  "EE + 2": "employee2Dependents",
  SP: "spouseOnly",
  CH: "childOnly",
  "SP + DP": "spouseDependent",
  "SP + CH": "spouseChildren",
  "Not Disclosed": "notDisclose",
};

const COVERAGE_ABBREVIATION_MAPPING = {
  ee: "employee",
  sp: "spouseOnly",
  ch: "childOnly",
} as const;

export default formatEcosystem;
